import { usePageRefresh } from '../../../../context/PageRefreshContext';
import { useState, useEffect, useMemo, useCallback } from 'react';
import { Icon } from '@iconify/react';
import { useDispatch, useSelector } from 'react-redux';
import { getInvoiceReport } from '../../../../redux/actions/SocietyPortal/InvoiceReportAction/InvoiceReportAction';
import { getInvoiceTypes } from '../../../../redux/actions/SocietyPortal/InvoiceTypeAction/InvoiceTypeAction';
import { getWingsDropdown, getFlatsDropdown } from '../../../../redux/actions/WingsAndFlatsActions/WingsAndFlatsAction';
import { exportInvoiceReportReq } from '../../../../axios/request/SocietyPortal/InvoiceReportReqs/InvoiceReportReqs';
import { selectedSocietyStorer } from '../../../../utils/webStorer';
import { PaginationLimit } from '../../../../components/PaginationLimit';

const STATUS_TABS = [
    { label: 'Posted', value: 'POSTED' },
    { label: 'Draft', value: 'DRAFT' },
    { label: 'Deleted', value: 'DELETED' },
];

const B2B_OPTIONS = [
    { label: 'All', value: 'ALL' },
    { label: 'B2B', value: 'B2B' },
    { label: 'B2C', value: 'B2C' },
];

const SEARCH_BY_OPTIONS = [
    { label: 'Bill Date', value: 'invoice_date' },
    { label: 'Created Date', value: 'generation_date' },
];

const InvoiceReport = () => {
    const dispatch = useDispatch();
    const societyId = selectedSocietyStorer.getSocietyId();

    const { reportData, headColumns, reportPagination, loading } = useSelector(state => state.InvoiceReportReducer || {});
    const { invoiceTypesData } = useSelector(state => state.InvoiceTypeReducers || {});
    const { wingsDropdown, flatsDropdown } = useSelector(state => state.WingsAndFlatsReducers || {});

    const [filters, setFilters] = useState({
        status_filter: 'POSTED',
        invoice_type_id: '',
        wing_id: '',
        flat_id: '',
        from_date: '',
        to_date: '',
        search_by: 'invoice_date',
        b2b_b2c: 'ALL',
        search: '',
    });
    const [page, setPage] = useState(1);
    const [limit, setLimit] = useState(20);
    const [exporting, setExporting] = useState(false);

    // Fetch dropdowns on mount
    useEffect(() => {
        dispatch(getInvoiceTypes({ society_id: societyId, page: 1, limit: 100 }));
        dispatch(getWingsDropdown({ society_id: societyId }));
    }, [dispatch, societyId]);

    // Fetch flats when wing changes
    useEffect(() => {
        if (filters.wing_id) {
            dispatch(getFlatsDropdown({ wing_id: filters.wing_id }));
        }
    }, [dispatch, filters.wing_id]);

    // Fetch report data
    const fetchReport = useCallback(() => {
        const params = { society_id: societyId, page, limit };
        if (filters.status_filter) params.status_filter = filters.status_filter;
        if (filters.invoice_type_id) params.invoice_type_id = filters.invoice_type_id;
        if (filters.wing_id) params.wing_id = filters.wing_id;
        if (filters.flat_id) params.flat_id = filters.flat_id;
        if (filters.from_date) params.from_date = filters.from_date;
        if (filters.to_date) params.to_date = filters.to_date;
        if (filters.search_by) params.search_by = filters.search_by;
        if (filters.b2b_b2c && filters.b2b_b2c !== 'ALL') params.b2b_b2c = filters.b2b_b2c;
        if (filters.search) params.search = filters.search;
        dispatch(getInvoiceReport(params));
    }, [dispatch, societyId, filters, page, limit]);
    usePageRefresh(fetchReport);

    useEffect(() => {
        fetchReport();
    }, [fetchReport]);

    const handleFilterChange = (name, value) => {
        setFilters(prev => {
            const updated = { ...prev, [name]: value };
            if (name === 'wing_id') updated.flat_id = '';
            return updated;
        });
        setPage(1);
    };

    const handleSearch = (e) => {
        handleFilterChange('search', e.target.value);
    };

    const handleExport = async () => {
        setExporting(true);
        try {
            const params = { society_id: societyId };
            if (filters.status_filter) params.status_filter = filters.status_filter;
            if (filters.invoice_type_id) params.invoice_type_id = filters.invoice_type_id;
            if (filters.wing_id) params.wing_id = filters.wing_id;
            if (filters.flat_id) params.flat_id = filters.flat_id;
            if (filters.from_date) params.from_date = filters.from_date;
            if (filters.to_date) params.to_date = filters.to_date;
            if (filters.search_by) params.search_by = filters.search_by;
            if (filters.b2b_b2c && filters.b2b_b2c !== 'ALL') params.b2b_b2c = filters.b2b_b2c;
            if (filters.search) params.search = filters.search;

            const response = await exportInvoiceReportReq(params);
            const blob = new Blob([response], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = 'invoice_report.xlsx';
            a.click();
            window.URL.revokeObjectURL(url);
        } catch (e) {
            console.error('Export failed:', e);
        } finally {
            setExporting(false);
        }
    };

    const handleReset = () => {
        setFilters({
            status_filter: 'POSTED',
            invoice_type_id: '',
            wing_id: '',
            flat_id: '',
            from_date: '',
            to_date: '',
            search_by: 'invoice_date',
            b2b_b2c: 'ALL',
            search: '',
        });
        setPage(1);
    };

    const invoices = reportData || [];
    // Dedupe heads by name (case-insensitive, trimmed) — BE returns one
    // tariff-head row per configured head, and if the society has multiple
    // heads with the same display name (e.g. GST configured on both
    // Maintenance and Facility invoice types) each would render as its
    // own column. Amounts are already grouped by head_name in
    // invoice.head_amounts on the BE, so collapsing here is a pure display
    // fix — no data loss.
    const heads = useMemo(() => {
        const src = headColumns || [];
        const seen = new Set();
        const out = [];
        for (const h of src) {
            const key = (h.head_name || '').trim().toLowerCase();
            if (!key || seen.has(key)) continue;
            seen.add(key);
            out.push(h);
        }
        return out;
    }, [headColumns]);
    const pagination = reportPagination || {};
    const wings = wingsDropdown || [];
    const flats = Array.isArray(flatsDropdown) ? flatsDropdown : flatsDropdown?.data || [];
    const invoiceTypes = invoiceTypesData || [];

    const formatCurrency = (amount) => {
        const num = parseFloat(amount || 0);
        return num.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    };

    const formatDate = (dateStr) => {
        if (!dateStr) return '-';
        const d = new Date(dateStr);
        return d.toLocaleDateString('en-IN', { day: '2-digit', month: '2-digit', year: 'numeric' });
    };

    const getStatusBadge = (status) => {
        const styles = {
            Paid: 'tw-bg-emerald-50 tw-text-emerald-700',
            Unpaid: 'tw-bg-red-50 tw-text-red-700',
            Partial: 'tw-bg-amber-50 tw-text-amber-700',
        };
        return (
            <span className={`tw-inline-flex tw-items-center tw-px-2 tw-py-0.5 tw-rounded tw-text-[11px] tw-font-medium ${styles[status] || 'tw-bg-gray-50 tw-text-gray-600'}`}>
                {status}
            </span>
        );
    };

    // Build dynamic columns
    const fixedColsBefore = [
        { header: 'Sr No', key: 'index', align: 'center', width: '50px' },
        { header: 'Bill No', key: 'bill_no' },
        { header: 'Invoice Type', key: 'invoice_type' },
        { header: 'Wing', key: 'wing', align: 'center' },
        { header: 'Unit No', key: 'unit_no', align: 'center' },
        { header: 'Member Name', key: 'member_name' },
        { header: 'GST No', key: 'gst_no' },
        { header: 'Bill Date', key: 'bill_date' },
    ];

    const headCols = heads.map(h => ({
        header: h.head_name,
        key: `head_${h.head_id}`,
        headName: h.head_name,
        align: 'right',
    }));

    const fixedColsAfter = [
        { header: 'Subtotal', key: 'subtotal', align: 'right' },
        { header: 'Tax', key: 'tax_amount', align: 'right' },
        { header: 'Grand Total', key: 'grand_total', align: 'right' },
        { header: 'Receipt Date', key: 'receipt_date' },
        { header: 'Pay Mode', key: 'payment_mode', align: 'center' },
        { header: 'Transaction No', key: 'transaction_number' },
        { header: 'Receipt Amt', key: 'receipt_amount', align: 'right' },
        { header: 'Bank', key: 'bank_name' },
        { header: 'Balance Due', key: 'balance_due', align: 'right' },
        { header: 'Status', key: 'status', align: 'center' },
    ];

    const allColumns = useMemo(() => [...fixedColsBefore, ...headCols, ...fixedColsAfter], [heads]);

    const getCellValue = (invoice, col, rowIndex) => {
        if (col.key === 'index') return (page - 1) * limit + rowIndex + 1;
        if (col.key === 'bill_date') return formatDate(invoice.bill_date);
        if (col.key === 'receipt_date') return formatDate(invoice.receipt_date);
        if (col.key === 'status') return getStatusBadge(invoice.status);
        if (col.headName) return formatCurrency(invoice.head_amounts?.[col.headName] || 0);
        if (['subtotal', 'tax_amount', 'grand_total', 'receipt_amount', 'balance_due'].includes(col.key)) {
            return formatCurrency(invoice[col.key]);
        }
        return invoice[col.key] || '-';
    };

    return (
        <>
            {/* Header */}
            <div className="tw-flex tw-items-start tw-justify-between tw-mb-4">
                <div>
                    <h1 className="tw-text-lg tw-font-bold tw-text-gray-800">Invoice Report</h1>
                    <p className="tw-text-xs tw-text-gray-400 tw-mt-0.5">View and export invoice data with filters</p>
                </div>
                <div className="tw-flex tw-items-center tw-gap-2">
                    <button
                        onClick={handleExport}
                        disabled={exporting || loading}
                        className="tw-flex tw-items-center tw-gap-1.5 tw-px-3 tw-py-1.5 tw-text-xs tw-font-medium tw-text-gray-600 tw-border tw-border-gray-200 tw-rounded-lg hover:tw-bg-gray-50 tw-transition-colors disabled:tw-opacity-50"
                    >
                        {exporting ? (
                            <Icon icon="mdi:loading" className="tw-text-sm tw-animate-spin" />
                        ) : (
                            <Icon icon="mdi:file-excel-outline" className="tw-text-sm" />
                        )}
                        {exporting ? 'Exporting...' : 'Export Excel'}
                    </button>
                </div>
            </div>

            {/* Status Tabs */}
            <div className="tw-flex tw-items-center tw-gap-1 tw-mb-4 tw-border-b tw-border-gray-200">
                {STATUS_TABS.map(tab => (
                    <button
                        key={tab.value}
                        onClick={() => handleFilterChange('status_filter', tab.value)}
                        className={`tw-px-4 tw-py-2 tw-text-xs tw-font-medium tw-border-b-2 tw-transition-colors ${
                            filters.status_filter === tab.value
                                ? 'tw-border-blue-600 tw-text-blue-600'
                                : 'tw-border-transparent tw-text-gray-500 hover:tw-text-gray-700'
                        }`}
                    >
                        {tab.label}
                    </button>
                ))}
            </div>

            {/* Filters */}
            <div className="tw-bg-white tw-rounded-lg tw-border tw-border-gray-200 tw-p-4 tw-mb-4">
                <div className="tw-grid tw-grid-cols-2 md:tw-grid-cols-3 lg:tw-grid-cols-6 tw-gap-3">
                    {/* Invoice Type */}
                    <div>
                        <label className="tw-block tw-text-[11px] tw-font-medium tw-text-gray-500 tw-mb-1">Invoice Type</label>
                        <select
                            value={filters.invoice_type_id}
                            onChange={(e) => handleFilterChange('invoice_type_id', e.target.value)}
                            className="tw-w-full tw-border tw-border-gray-300 tw-rounded-md tw-px-2.5 tw-py-1.5 tw-text-xs focus:tw-outline-none focus:tw-ring-1 focus:tw-ring-blue-500"
                        >
                            <option value="">All Types</option>
                            {invoiceTypes.map(t => (
                                <option key={t.invoice_type_id} value={t.invoice_type_id}>{t.invoice_title}</option>
                            ))}
                        </select>
                    </div>

                    {/* Wing */}
                    <div>
                        <label className="tw-block tw-text-[11px] tw-font-medium tw-text-gray-500 tw-mb-1">Wing</label>
                        <select
                            value={filters.wing_id}
                            onChange={(e) => handleFilterChange('wing_id', e.target.value)}
                            className="tw-w-full tw-border tw-border-gray-300 tw-rounded-md tw-px-2.5 tw-py-1.5 tw-text-xs focus:tw-outline-none focus:tw-ring-1 focus:tw-ring-blue-500"
                        >
                            <option value="">All Wings</option>
                            {wings.map(w => (
                                <option key={w.wing_id} value={w.wing_id}>{w.title}</option>
                            ))}
                        </select>
                    </div>

                    {/* Flat */}
                    <div>
                        <label className="tw-block tw-text-[11px] tw-font-medium tw-text-gray-500 tw-mb-1">Flat</label>
                        <select
                            value={filters.flat_id}
                            onChange={(e) => handleFilterChange('flat_id', e.target.value)}
                            disabled={!filters.wing_id}
                            className="tw-w-full tw-border tw-border-gray-300 tw-rounded-md tw-px-2.5 tw-py-1.5 tw-text-xs focus:tw-outline-none focus:tw-ring-1 focus:tw-ring-blue-500 disabled:tw-bg-gray-50 disabled:tw-text-gray-400"
                        >
                            <option value="">All Flats</option>
                            {flats.map(f => (
                                <option key={f.flat_id} value={f.flat_id}>{f.flat_number || f.flat_name}</option>
                            ))}
                        </select>
                    </div>

                    {/* From Date */}
                    <div>
                        <label className="tw-block tw-text-[11px] tw-font-medium tw-text-gray-500 tw-mb-1">From Date</label>
                        <input
                            type="date"
                            value={filters.from_date}
                            onChange={(e) => handleFilterChange('from_date', e.target.value)}
                            className="tw-w-full tw-border tw-border-gray-300 tw-rounded-md tw-px-2.5 tw-py-1.5 tw-text-xs focus:tw-outline-none focus:tw-ring-1 focus:tw-ring-blue-500"
                        />
                    </div>

                    {/* To Date */}
                    <div>
                        <label className="tw-block tw-text-[11px] tw-font-medium tw-text-gray-500 tw-mb-1">To Date</label>
                        <input
                            type="date"
                            value={filters.to_date}
                            onChange={(e) => handleFilterChange('to_date', e.target.value)}
                            className="tw-w-full tw-border tw-border-gray-300 tw-rounded-md tw-px-2.5 tw-py-1.5 tw-text-xs focus:tw-outline-none focus:tw-ring-1 focus:tw-ring-blue-500"
                        />
                    </div>

                    {/* Search By */}
                    <div>
                        <label className="tw-block tw-text-[11px] tw-font-medium tw-text-gray-500 tw-mb-1">Search By</label>
                        <select
                            value={filters.search_by}
                            onChange={(e) => handleFilterChange('search_by', e.target.value)}
                            className="tw-w-full tw-border tw-border-gray-300 tw-rounded-md tw-px-2.5 tw-py-1.5 tw-text-xs focus:tw-outline-none focus:tw-ring-1 focus:tw-ring-blue-500"
                        >
                            {SEARCH_BY_OPTIONS.map(o => (
                                <option key={o.value} value={o.value}>{o.label}</option>
                            ))}
                        </select>
                    </div>
                </div>

                {/* Second row: B2B/B2C, Search, Reset */}
                <div className="tw-flex tw-items-end tw-gap-3 tw-mt-3">
                    <div>
                        <label className="tw-block tw-text-[11px] tw-font-medium tw-text-gray-500 tw-mb-1">B2B / B2C</label>
                        <select
                            value={filters.b2b_b2c}
                            onChange={(e) => handleFilterChange('b2b_b2c', e.target.value)}
                            className="tw-border tw-border-gray-300 tw-rounded-md tw-px-2.5 tw-py-1.5 tw-text-xs focus:tw-outline-none focus:tw-ring-1 focus:tw-ring-blue-500"
                        >
                            {B2B_OPTIONS.map(o => (
                                <option key={o.value} value={o.value}>{o.label}</option>
                            ))}
                        </select>
                    </div>

                    <div className="tw-flex-1 tw-max-w-xs">
                        <label className="tw-block tw-text-[11px] tw-font-medium tw-text-gray-500 tw-mb-1">Search</label>
                        <div className="tw-relative">
                            <Icon icon="mdi:magnify" className="tw-absolute tw-left-2.5 tw-top-1/2 tw--translate-y-1/2 tw-text-gray-400 tw-text-sm" />
                            <input
                                type="text"
                                placeholder="Bill no or member name..."
                                value={filters.search}
                                onChange={handleSearch}
                                className="tw-w-full tw-border tw-border-gray-300 tw-rounded-md tw-pl-8 tw-pr-3 tw-py-1.5 tw-text-xs focus:tw-outline-none focus:tw-ring-1 focus:tw-ring-blue-500"
                            />
                        </div>
                    </div>

                    <button
                        onClick={handleReset}
                        className="tw-flex tw-items-center tw-gap-1 tw-px-3 tw-py-1.5 tw-text-xs tw-font-medium tw-text-gray-500 tw-border tw-border-gray-200 tw-rounded-md hover:tw-bg-gray-50"
                    >
                        <Icon icon="mdi:refresh" className="tw-text-sm" />
                        Reset
                    </button>
                </div>
            </div>

            {/* Table */}
            <div className="tw-bg-white tw-rounded-lg tw-border tw-border-gray-200 tw-overflow-hidden">
                <div className="tw-overflow-x-auto">
                    <table className="tw-w-full tw-text-xs">
                        <thead>
                            <tr className="tw-bg-gray-50 tw-border-b tw-border-gray-200">
                                {allColumns.map(col => (
                                    <th
                                        key={col.key}
                                        className="tw-px-3 tw-py-2.5 tw-text-[11px] tw-font-semibold tw-text-gray-600 tw-uppercase tw-tracking-wider tw-whitespace-nowrap"
                                        style={{ textAlign: col.align || 'left', minWidth: col.width || 'auto' }}
                                    >
                                        {col.header}
                                    </th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {loading ? (
                                <tr>
                                    <td colSpan={allColumns.length} className="tw-text-center tw-py-12">
                                        <Icon icon="mdi:loading" className="tw-animate-spin tw-text-2xl tw-text-gray-300 tw-mx-auto" />
                                        <p className="tw-text-xs tw-text-gray-400 tw-mt-2">Loading report...</p>
                                    </td>
                                </tr>
                            ) : invoices.length === 0 ? (
                                <tr>
                                    <td colSpan={allColumns.length} className="tw-text-center tw-py-12">
                                        <Icon icon="mdi:file-document-outline" className="tw-text-4xl tw-text-gray-200 tw-mx-auto" />
                                        <p className="tw-text-xs tw-text-gray-400 tw-mt-2">No invoices found</p>
                                    </td>
                                </tr>
                            ) : (
                                invoices.map((invoice, idx) => (
                                    <tr key={invoice.invoice_id} className="tw-border-b tw-border-gray-100 hover:tw-bg-gray-50/50">
                                        {allColumns.map(col => (
                                            <td
                                                key={col.key}
                                                className="tw-px-3 tw-py-2 tw-whitespace-nowrap tw-text-gray-700"
                                                style={{ textAlign: col.align || 'left' }}
                                            >
                                                {getCellValue(invoice, col, idx)}
                                            </td>
                                        ))}
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* Pagination */}
            {pagination.total > 0 && (
                <PaginationLimit
                    currentPage={page}
                    totalRecords={pagination.total || 0}
                    limit={limit}
                    handleLimitChange={(e) => { setLimit(Number(e.target.value)); setPage(1); }}
                    handlePageChange={(p) => setPage(p)}
                />
            )}
        </>
    );
};

export default InvoiceReport;
